import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Skeleton,
} from '@sitewise/ui';
import { useQuery } from '@tanstack/react-query';
import { AlertOctagon, CalendarDays, ClipboardList, FileCheck2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { fetchInspectionDashboard } from '../../../api/inspections.js';

import { formatDate, formatRelativeDays, StatusBadge } from './lib.js';

export function InspectionsDashboardPage() {
  const dashQuery = useQuery({
    queryKey: ['inspections', 'dashboard'],
    queryFn: fetchInspectionDashboard,
  });

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Upcoming (next 7d)"
          value={dashQuery.data?.upcomingCount}
          loading={dashQuery.isLoading}
          icon={CalendarDays}
          link="/inspections/runs?status=scheduled"
        />
        <StatCard
          label="Defects awaiting WO"
          value={dashQuery.data?.defectsAwaitingWoCount}
          loading={dashQuery.isLoading}
          icon={AlertOctagon}
          link="/inspections/runs?status=submitted"
        />
        <StatCard
          label="Run inspections"
          value={null}
          loading={false}
          icon={ClipboardList}
          link="/inspections/runs"
          hint="Tablet-friendly runner →"
        />
        <StatCard
          label="Templates"
          value={null}
          loading={false}
          icon={FileCheck2}
          link="/inspections/templates"
          hint="Build a Notion-like form →"
        />
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <CardTitle className="text-base">Next 3 scheduled</CardTitle>
          <Link
            to="/inspections/runs?status=scheduled"
            className="text-xs text-primary hover:underline"
          >
            View all →
          </Link>
        </CardHeader>
        <CardContent>
          {dashQuery.isLoading ? (
            <SkeletonRows />
          ) : (dashQuery.data?.nextThree.length ?? 0) === 0 ? (
            <EmptyState
              icon={CalendarDays}
              title="No upcoming inspections"
              description="Create a schedule or kick off a one-off inspection to populate this list."
              action={
                <Button asChild size="sm">
                  <Link to="/inspections/runs">Run an inspection</Link>
                </Button>
              }
            />
          ) : (
            <ul className="divide-y divide-border">
              {dashQuery.data!.nextThree.map((insp) => (
                <li key={insp.id} className="flex items-center justify-between py-3">
                  <div className="min-w-0">
                    <Link
                      to={`/inspections/runs/${insp.id}`}
                      className="block truncate text-sm font-medium hover:underline"
                    >
                      Inspection {insp.id.slice(0, 8)}
                    </Link>
                    <div className="text-xs text-fg-muted">
                      {formatDate(insp.scheduledFor)} · {formatRelativeDays(insp.scheduledFor)}
                    </div>
                  </div>
                  <StatusBadge status={insp.status} />
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({
  label,
  value,
  loading,
  icon: Icon,
  link,
  hint,
}: {
  label: string;
  value: number | null | undefined;
  loading: boolean;
  icon: typeof CalendarDays;
  link: string;
  hint?: string;
}) {
  return (
    <Link to={link} className="block">
      <Card className="transition-colors hover:border-primary">
        <CardContent className="flex items-center justify-between p-4">
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-fg-muted">{label}</div>
            <div className="mt-1 text-2xl font-semibold">
              {loading ? <Skeleton className="h-7 w-12" /> : (value ?? '—')}
            </div>
            {hint ? <div className="mt-1 text-xs text-fg-muted">{hint}</div> : null}
          </div>
          <Icon className="h-6 w-6 text-fg-muted" />
        </CardContent>
      </Card>
    </Link>
  );
}

function SkeletonRows() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-12 w-full" />
    </div>
  );
}
